import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as _ from 'lodash';

import { WikiConflictModalComponent, WikiConflictInfo } from '../conflict-modal/wiki.conflict-modal.component';

/** Matches WikiService::CONFLICT_ERROR on the backend. */
export const WIKI_CONFLICT_ERROR = 'wiki_conflict';

/** Matches WikiService::BASE_VERSION_REQUIRED_ERROR on the backend. */
export const WIKI_BASE_VERSION_REQUIRED_ERROR = 'wiki_base_version_required';

/** What the user chose in the conflict modal. */
export type WikiConflictChoice = 'overwrite' | 'keep-editing';

/**
 * Recognises the 409 returned by `POST /api/wiki` when the page moved on during editing, and
 * shows the user what happened.
 *
 * Shared by every wiki save path — the routed editor, `<wiki-container>`, `<gf-notebook>` and
 * version restore — so all of them report a conflict the same way.
 */
@Injectable()
export class WikiConflictService {

    constructor(private modalService: NgbModal) {}

    /**
     * Extract the conflict payload from a failed save, or null if this wasn't a conflict.
     *
     * See {@link parseBody} for how the body is read. A 409 we can't read at all is still
     * reported as a conflict rather than a generic failure — hence the minimal fallback object.
     */
    parse(error: any): WikiConflictInfo | null {
        if (!error || (error as HttpErrorResponse).status !== 409) { return null; }

        const body = this.parseBody(error);

        if (!body || typeof body !== 'object') {
            return {
                error: WIKI_CONFLICT_ERROR,
                pageId: 0,
                pagename: '',
                baseVersion: 0,
                currentVersion: 0,
                currentAuthor: null,
                currentSavedAt: null,
            };
        }

        return {
            error: _.get(body, 'error', WIKI_CONFLICT_ERROR),
            pageId: _.get(body, 'pageId', 0),
            pagename: _.get(body, 'pagename', ''),
            baseVersion: _.get(body, 'baseVersion', 0),
            currentVersion: _.get(body, 'currentVersion', 0),
            currentAuthor: body?.currentAuthor ?? null,
            currentSavedAt: body?.currentSavedAt ?? null,
        };
    }

    /**
     * The server's own explanation for a refused save, when it sent one of its structured wiki
     * save errors. Most useful for the 400 a create gets when the page name is already taken:
     * "that page already exists" tells the user how to proceed, where a generic failure does not.
     *
     * Returns null for anything else, so callers keep their own wording for ordinary failures.
     */
    parseRejectionMessage(error: any): string | null {
        const body = this.parseBody(error);
        if (!body || typeof body !== 'object') { return null; }

        const code = _.get(body, 'error', '');
        if (code !== WIKI_BASE_VERSION_REQUIRED_ERROR && code !== WIKI_CONFLICT_ERROR) { return null; }

        return _.get(body, 'message', '') || null;
    }

    /**
     * The error body as an object. The server sends JSON with `Content-Type: text/plain` (it
     * rides as the message of a generic RestException), which HttpClient still parses because
     * the request's responseType is json — but a proxy or an error page can leave it a string,
     * so both shapes are handled.
     */
    private parseBody(error: any): any {
        let body = (error as HttpErrorResponse)?.error;
        if (typeof body === 'string') {
            try { body = JSON.parse(body); } catch (e) { body = null; }
        }
        return body;
    }

    /**
     * Show the conflict modal. Resolves with the user's choice; dismissing it (Esc, backdrop)
     * counts as 'keep-editing' so no dismissal path can discard their work.
     */
    open(conflict: WikiConflictInfo, wikiId: number, allowOverwrite: boolean = true): Promise<WikiConflictChoice> {
        const modalRef: NgbModalRef = this.modalService.open(WikiConflictModalComponent, {
            backdrop: 'static',
            keyboard: false,
            size: 'lg',
        });
        modalRef.componentInstance.conflict = conflict;
        modalRef.componentInstance.wikiId = wikiId || conflict.pageId;
        modalRef.componentInstance.allowOverwrite = allowOverwrite;

        return modalRef.result.then(
            (result) => (result === 'overwrite' ? 'overwrite' : 'keep-editing'),
            () => 'keep-editing' as WikiConflictChoice,
        );
    }
}
